import React, { FC } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import IdeaForm from 'buyside/components/presentation/IdeaForm/ideaForm';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ResearchSubmissionQuery from 'buyside/components/presentation/ResearchSubmissionPage/researchSubmissionQuery';
import ResearchTypeSelector from 'buyside/components/presentation/ResearchSubmissionPage/ResearchTypeSelector/researchTypeSelector';
import WhitePaperForm from 'buyside/components/presentation/WhitePaperForm/whitePaperForm';

import { NEW_RESEARCH_ROUTES } from 'buyside/util/urlHelper';

import {
  useCurrentUserResearchSubmissionQuery,
  useIdeaSubmissionQuery,
  useWhitePaperSubmissionQuery
} from 'interfaces/graphql-typings';

export const RESEARCH_SUBMISSION_TYPES = {
  IDEA: {
    name: 'Idea',
    description: 'Write your recommendation on one more more securities.',
    route: NEW_RESEARCH_ROUTES.IDEA
  },
  MACRO_THEMATIC: {
    name: 'Macro/Thematic',
    description: 'My report is not security specific.',
    route: NEW_RESEARCH_ROUTES.MACRO_THEMATIC
  }
};

const ResearchSubmissionPage: FC = () => {
  const { data, loading, error } = useCurrentUserResearchSubmissionQuery();

  const { state } = useLocation();

  if (loading || !data?.currentUser) return <LoadingSpinner />;
  if (error) throw error;

  const { canWriteIdeas, canWriteWhitePapers } = data.currentUser;
  const availableResearchTypes = [];

  if (canWriteIdeas) availableResearchTypes.push(RESEARCH_SUBMISSION_TYPES.IDEA);
  if (canWriteWhitePapers) availableResearchTypes.push(RESEARCH_SUBMISSION_TYPES.MACRO_THEMATIC);

  if (state?.cannotEditError) toast.error(state.cannotEditError, { theme: 'colored' });

  return (
    <Routes>
      {/* RESEARCH SUBMISSION LANDING PAGE: pro/research/new */}
      <Route path="/" element={<ResearchTypeSelector researchTypes={availableResearchTypes} />} />
      {/* CREATE/EDIT IDEA: pro/research/new/idea or pro/research/new/idea/:ideaId */}
      <Route
        path={NEW_RESEARCH_ROUTES.IDEA + '/:id?'}
        element={
          canWriteIdeas ? (
            <ResearchSubmissionQuery useQuery={useIdeaSubmissionQuery} component={IdeaForm} />
          ) : (
            <Navigate
              to="/"
              state={{
                cannotEditError:
                  'You are unable to submit ideas. Please contact support@sumzero.com if you believe this is an error.'
              }}
            />
          )
        }
      />
      {/* CREATE/EDIT WHITE PAPER: pro/research/new/macro_thematic or pro/research/new/macro_thematic/:id */}
      <Route
        path={NEW_RESEARCH_ROUTES.MACRO_THEMATIC + '/:id?'}
        element={
          canWriteWhitePapers ? (
            <ResearchSubmissionQuery
              useQuery={useWhitePaperSubmissionQuery}
              component={WhitePaperForm}
            />
          ) : (
            <Navigate
              to="/"
              state={{
                cannotEditError:
                  'You are unable to submit Macro / Thematic reports. Please contact support@sumzero.com if you believe this is an error.'
              }}
            />
          )
        }
      />
    </Routes>
  );
};
export default ResearchSubmissionPage;
